import { rpc } from "src/composables/Rpc";
import { CoachTitle, DateTimelike, Guid, Integerlike, Numbool, MinSeason, MinTeam, MinDivision } from "src/interfaces/InleagueApiV1";
import { NoUncheckedIndexedAccess } from "src/helpers/utils";

export interface CoachAssignmentMenuOption {
  season: MinSeason,
  team: MinTeam,
}

export const getCoachAssignmentMenuOptions = rpc<void, CoachAssignmentMenuOption[]>("get", "v1/coachAssignments/getCoachAssignmentMenuOptions")

type __fixme__ensureBackendFormatsDateProperly = DateTimelike

export interface CoachAssignmentUser {
  userID: Guid,
  firstName: string,
  lastName: string,
  email: string,
  primaryPhone: string,
  riskStatus: string,
  riskStatusExpiration: DateTimelike,
  riskStatusCoachResult: {
    description: string,
    color: string,
    access : boolean,
    /**
     * if true, cannot have coach assignments / volunteer assignments created
     */
    forbidden: boolean,
  },
  lastLogin: "" | DateTimelike,
  volunteerCertifications: VolunteerCertificate[],
  seasonalCoachAssignments: CoachAssignment[],
  seasonalVolunteerAssignments: VolunteerAssignment[],
  seasonalYouthCoachAssignments: YouthCoachAssignment[],
  seasonalPlayers: CoachAssignmentsPlayerAssignment[],
  coachReportRowColoringInfo: {
    userHasSomeCoachCertButHasNotVolunteeredAsACoachThisSeasonDiv: boolean,
    userHasVolunteeredAsACoachForThisDiv: boolean,
    userHasVolunteeredAsACoachForThisDivAndHasSomeCoachCert: boolean,
    userHasVolunteeredAsACoachForThisDivAndHasSomeCoachCertAndHasSomeCoachAssignment: boolean,
  }
}

export const listCoachAssignmentVolunteers = rpc<{teamID: Guid, seasonUID: Guid}, CoachAssignmentUser[]>("get", "v1/coachAssignments/listCoachAssignmentVolunteers")

export interface CoachAssignmentsTeamSummaryTeam {
  teamID: Guid,
  teamName: string,
  teamDesignation: string,
  division: MinDivision,
  players: CoachAssignmentsPlayerAssignment[],
  coaches: CoachAssignment[],
}

export interface CoachAssignmentsPlayerAssignment {
  assignmentID: Guid,
  seasonUID: Guid,
  team: MinTeam,
  childID: Guid,
  playerFirstName: string,
  playerLastName: string,
  playerAge: number,
  parent1: CoachAssignmentsTeamSummaryTeam_ParentInfo
  parent2: CoachAssignmentsTeamSummaryTeam_ParentInfo | null
}

// TODO: shouldn't this be (can it be?) a CoachAssignmentUser? Except we'd need to break the cycle of CoachAssignmentUser->players->parent{1,2}->players->parent{1,2}...
// so "players" would be optional on CoachAssignmentUser
export interface CoachAssignmentsTeamSummaryTeam_ParentInfo {
  userID: Guid,
  firstName: string,
  lastName: string,
  coachAssignments: CoachAssignment[],
  volunteerAssignments: VolunteerAssignment[],
  volunteerIntents: VolunteerIntent[],
}

export interface CoachAssignment {
  assignmentID: Guid,
  seasonUID: Guid,
  team: MinTeam,
  title: CoachTitle,
  user: {
    userID: Guid,
    firstName: string,
    lastName: string,
  }
}

export interface VolunteerAssignment {
  assignmentID: Guid,
  seasonUID: Guid,
  team: MinTeam,
  positionID: Guid,
  codeName: string,
  user: {
    userID: Guid,
    firstName: string,
    lastName: string,
  }
}

export interface VolunteerIntent {
  seasonUID: Guid,
  divID: Guid,
  codeID: Guid,
  code: string,
  user: {
    userID: Guid,
    firstName: string,
    lastName: string,
  }
}

// TODO: fixup bools to be real bools, nest the "certdef" in the certificate, nest `user` obj as {userID, firstName, lastName}
export interface VolunteerCertificate {
  addedBy: Guid,
  category: string,
  certDate: __fixme__ensureBackendFormatsDateProperly,
  clientID: Guid,
  code: string,
  dateAdded: __fixme__ensureBackendFormatsDateProperly,
  provisional: Numbool,
  stackRecordKey: Guid,
  validated: Numbool,
  vCertID: Integerlike,
  userID: Guid,
  discipline: string,
  certificationDesc: string,
  isSafeHaven: "" | Numbool,
  isConcussion: "" | Numbool,
  requiresValidation: "" | Numbool
}

export interface YouthCoachAssignment {
  assignmentID: Guid,
  clientID: Guid,
  seasonUID: Guid,
  team: MinTeam,
  user: {
    userID: Guid,
    firstName: string,
    lastName: string,
  }
}

export const getCoachAssignmentsTeamsSummary = rpc<{seasonUID: Guid, competitionUID: Guid, divID: Guid}, CoachAssignmentsTeamSummaryTeam[]>("get", "v1/coachAssignments/getTeamsSummary")

export const listCoachAssignmentCandidates = rpc<{
  seasonUID: Guid,
  competitionUID: Guid,
  divID: Guid,
  requireHasChildInDivSeason: boolean,
}, CoachAssignmentUser[]>("get", "v1/coachAssignments/listCoachAssignmentCandidates")
export const searchCoachAssignmentCandidates = rpc<{
  seasonUID: Guid,
  competitionUID: Guid,
  divID: Guid,
  searchText: string,
  requireHasChildInDivSeason: boolean,
}, CoachAssignmentUser[]>("get", "v1/coachAssignments/searchCoachAssignmentCandidates")

export interface AssignableCodesForUsers {
  coachCodesByUserID: {[userID: Guid]: NoUncheckedIndexedAccess | {label: string, value: CoachTitle}[]},
  nonCoachOrFlexCodes: {
    label: string,
    value: (typeof NonCoachOrFlexCodes)["Youth"] | (typeof NonCoachOrFlexCodes)["Referee"]
  }[],
  flexCodes: {label: string, value: Guid}[],
}

/**
 * get what codes are assignable per user.
 * Takes into consideration "the current user" wrt to their permission to assign codes to some particular user.
 * DOES NOT take into consideration "codes already assigned and would produce duplicates", so frontend needs to check for that
 * before offering the options for each user.
 */
export const getAssignableCodesForUsers = rpc<{seasonUID: Guid, teamID: Guid, userIDs: Guid[]}, AssignableCodesForUsers>(
  "post", // post request to make it easier to work with the array arg
  "v1/coachAssignments/getAssignableCodesForUsers"
)

export const createCoachAndVolunteerAssignments = rpc<{seasonUID: Guid, codes: {userID: Guid, teamID: Guid, code: string}[]}, void>("post", "v1/coachAssignments/createCoachAndVolunteerAssignments")
export const deleteCoachAndVolunteerAssignments = rpc<{coachAssignmentIDs: Guid[], volunteerAssignmentIDs: Guid[], youthCoachAssignmentIDs: Guid[]}, void>("post", "v1/coachAssignments/deleteCoachAndVolunteerAssignments")

const NonCoachOrFlexCodes = {
  ["Head Coach" satisfies CoachTitle]: "Head Coach" satisfies CoachTitle, // physically stored in coach_assignments
  ["Co-Coach" satisfies CoachTitle]: "Co-Coach" satisfies CoachTitle, // physically stored in coach_assignments
  ["Assistant" satisfies CoachTitle]: "Assistant" satisfies CoachTitle, // physically stored in coach_assignments
  ["Administrator" satisfies CoachTitle]: "Administrator" satisfies CoachTitle, // physically stored in coach_assignments
  "Youth": "Youth", // youth coach, physically stored in youth_coach_assignments
  "Referee": "Referee" // referee, physically stored in volunteer_assignments, where there is assumed to be a flexcode having this name
} as const
